import * as React from 'react';
import { useState } from 'react';
import Modal from '@mui/material/Modal';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PortfolioModalLeft from './PortfolioModalLeft';
import PortfolioModalRight from './PortfolioModalRight';
import { useQuery } from 'react-query';
import PhotographersApi from '../photographers/PhotographersApi';
import PortfolioModalFullScreen from './PortfolioModalFullScreen';
import { handleError } from '../../shared/ErrorCatching';
import notify from '../../shared/feedback/notifications';
import { Translate } from '../../shared/LanguageService';
import IImage from './IImage';
import ILabels from './ILabels';
import IPhotographer from './IPhotographer';
import IPresentation from './IPresentation';

interface Props {
    presentation: IPresentation;
    images: IImage[];
    labels: ILabels;
    show: boolean;
    photographer: IPhotographer;
    onClose: () => void;
};

const styleModal = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    height: '100%',
    width: '100%',
    maxWidth: { xs: 'calc(100% - 20px)', xl: '70%' },
    maxHeight: { xs: 'calc(100% - 20px)', xl: '70%' },
    bgcolor: '#DEE3E2',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    overflowY: { xs: "auto", md: 'none' }
};

export const PortfolioModal: React.FunctionComponent<Props> = ({ presentation, images, photographer, show, onClose, labels }) => {

    const [fullscreenMode, setFullscreenMode] = useState(false)
    const [gridMode, setGridMode] = useState(false)
    const [currentImage, setCurrentImage] = useState(0)
    const handleClose = () => {
        setFullscreenMode(false)
        onClose()
    }
    const onNextImage = () => {
        if (currentImage == images.length - 1) {
            setCurrentImage(0)
        } else {
            setCurrentImage(currentImage + 1)
        }
    }

    const onBeforeImage = () => {
        if (currentImage == 0) {
            setCurrentImage(images.length - 1)
        } else {
            setCurrentImage(currentImage - 1)
        }
    }

    return (
        <>
            {presentation
                ? <Modal
                    open={show}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                    onKeyDown={(e) => {
                        e.preventDefault()
                        if (e.key === 'ArrowRight') {
                            onNextImage()
                        }

                        if (e.key === 'ArrowLeft') {
                            onBeforeImage()
                        }
                    }}
                >
                    <div>
                        {!fullscreenMode &&
                            <Grid sx={styleModal}
                                className='portfolio-modal-wrapper'
                                role="dialog"
                                aria-label='portfolio-modal'>
                                <Grid
                                    sx={{
                                        minWidth: '250px',
                                        p: '24px',
                                        borderRight: {
                                            xs: 'none',
                                            md: '1px solid rgba(0, 0, 0, 0.12)'
                                        },
                                        borderBottom: {
                                            xs: '1px solid rgba(0, 0, 0, 0.12)',
                                            md: 'none'
                                        },
                                        maxHeight: { md: '100%' },
                                        overflowY: { xs: "none", md: 'auto' },
                                        flexGrow: '1'
                                    }}>
                                    <PortfolioModalLeft presentation={presentation} photographer={photographer} />
                                </Grid>

                                <Grid sx={{
                                    width: { xs: '100%', md: 'auto' },
                                    height: { md: '100%' },
                                    maxWidth: { md: 'calc(100% - 250px)' },
                                    aspectRatio: '1.5',
                                    position: 'relative',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}>
                                    {images ?
                                        <PortfolioModalRight
                                            setFullscreenMode={setFullscreenMode}
                                            images={images}
                                            currentImage={currentImage}
                                            setCurrentImage={setCurrentImage}
                                            onNextImage={onNextImage}
                                            onBeforeImage={onBeforeImage}
                                            gridMode={gridMode}
                                            setGridMode={setGridMode}
                                            screenWidth={window.innerWidth}
                                            photographer={photographer}
                                            labels={labels}
                                        />
                                        : null}
                                </Grid>

                                <IconButton
                                    aria-label="close"
                                    onClick={handleClose}
                                    sx={{
                                        position: 'absolute',
                                        right: 13,
                                        top: 13,
                                        p: 0
                                    }}
                                >
                                    <CloseIcon
                                        sx={{
                                            color: '#ffffff',
                                            height: '24px',
                                            width: '24px',
                                            '&:hover': {
                                                color: 'primary.light'
                                            },
                                        }} />
                                </IconButton>
                            </Grid>
                        }

                        {fullscreenMode &&
                            <PortfolioModalFullScreen
                                setFullscreenMode={setFullscreenMode}
                                images={images}
                                currentImage={currentImage}
                                onNextImage={onNextImage}
                                onBeforeImage={onBeforeImage}
                            />
                        }
                    </div>
                </Modal>
                : null}
        </>
    )
}

function PortfolioModalDataLayer(props: any) {

    const labels = {
        serverImageError: Translate('portfolio/server-images-error'),
        noImagesPlaceholder: Translate('portfolio/no-images-placeholder')
    }

    const { photographer, show } = props;

    const { data: presentation } = useQuery('photographer_presentation', () => PhotographersApi.getPhotographerPresentation(Number(photographer.id)),
        { enabled: show }
    );

    const { data: favoriteImages } = useQuery('photographer_presentation_images', () => PhotographersApi.getPhotographerFavoritedImages(Number(photographer.id)),
        {
            enabled: show,
            onError: (error) => {
                handleError(error)
                notify.failure(
                    labels.serverImageError
                );
            }
        }
    );

    let images = favoriteImages ? favoriteImages : []

    return <PortfolioModal {...props} presentation={presentation} images={images} labels={labels} />
}

export default PortfolioModalDataLayer